<template>
    <div class="flex gap-1 items-center" data-testid="chat-message-reaction-bar">
        <BaseTooltip :trigger-aria-label="t('chat.reactions.thumbsUpTooltipTrigger')">
            <template #trigger>
                <BaseButton
                    :theme="thumbUpActive ? 'secondary' : 'tertiary'"
                    icon-only
                    :size="isNadiaChatRefreshEnabled ? 'sm' : 'md'"
                    :aria-label="t('chat.reactions.helpful')"
                    :aria-pressed="thumbUpActive"
                    :disabled="buttonsDisabled"
                    :class="hoverRevealClass"
                    @click="handleThumbUpClick">
                    <i v-if="isNadiaChatRefreshEnabled" class="bi bi-hand-thumbs-up" aria-hidden="true" />
                    <ThumbsUp v-else aria-hidden="true" />
                </BaseButton>
            </template>
            <template #content>
                {{ thumbUpActive ? t("chat.reactions.removeThumbsUp") : t("chat.reactions.goodResponse") }}
            </template>
        </BaseTooltip>
        <!-- Feedback issues modal (thumbs down) -->
        <BaseDialog v-model:open="showFeedbackIssuesDialog" :title="t('chat.reactions.whatWentWrong')" @close="dismissCommentPopover" @update:open="handleCommentPopoverChange">
            <FeedbackIssuesForm :target="feedbackTarget" embedded @save="handleFeedbackFormSave" @dismiss="dismissCommentPopover" />
            <template #trigger>
                <BaseTooltip :trigger-aria-label="t('chat.reactions.thumbsDownTooltipTrigger')">
                    <template #trigger>
                        <BaseButton
                            :theme="thumbDownActive ? 'secondary' : 'tertiary'"
                            icon-only
                            :size="isNadiaChatRefreshEnabled ? 'sm' : 'md'"
                            :aria-label="t('chat.reactions.notHelpful')"
                            :aria-pressed="thumbDownActive"
                            :disabled="buttonsDisabled"
                            :class="hoverRevealClass"
                            @click="handleThumbDownClick">
                            <i v-if="isNadiaChatRefreshEnabled" class="bi bi-hand-thumbs-down" aria-hidden="true" />
                            <ThumbsDown v-else aria-hidden="true" />
                        </BaseButton>
                    </template>
                    <template #content>
                        {{ thumbDownActive ? t("chat.reactions.removeThumbsDown") : t("chat.reactions.badResponse") }}
                    </template>
                </BaseTooltip>
            </template>
        </BaseDialog>

        <BaseTooltip v-if="isNadiaChatRefreshEnabled" :trigger-aria-label="t('chat.messageActions.dictationTooltipTrigger')">
            <template #trigger>
                <BaseButton
                    :theme="isReadingThisMessage ? 'secondary' : 'tertiary'"
                    icon-only
                    size="sm"
                    :aria-label="isReadingThisMessage ? t('chat.messageActions.pauseReading') : t('chat.messageActions.readAloud')"
                    :aria-pressed="isReadingThisMessage"
                    :class="hoverRevealClass"
                    @click="handleDictationToggle">
                    <i class="bi leading-none scale-140" :class="isReadingThisMessage ? 'bi-pause' : 'bi-volume-down'" aria-hidden="true"></i>
                </BaseButton>
            </template>
            <template #content>
                {{ isReadingThisMessage ? t("chat.messageActions.pause") : t("chat.messageActions.readAloud") }}
            </template>
        </BaseTooltip>
        <!-- Read-aloud transport controls, shown next to the pause button only while this message is being read. -->
        <template v-if="isNadiaChatRefreshEnabled && isReadingThisMessage">
            <BaseTooltip :trigger-aria-label="t('chat.messageActions.playbackSpeedTooltipTrigger')">
                <template #trigger>
                    <BaseButton
                        theme="tertiary"
                        size="sm"
                        :aria-label="t('chat.messageActions.playbackSpeedLabel', { speed: useChatStore.dictationSpeed })"
                        :class="hoverRevealClass"
                        @click="useChatStore.cycleDictationSpeed()">
                        <span class="scaffold-details-small" aria-hidden="true">{{ `${useChatStore.dictationSpeed}x` }}</span>
                    </BaseButton>
                </template>
                <template #content>{{ t("chat.messageActions.playbackSpeed") }}</template>
            </BaseTooltip>
        </template>

        <BaseTooltip :open="isCopyTooltipOpen" :delay-duration="0" :trigger-aria-label="t('chat.reactions.copyTooltipTrigger')" @update:open="handleCopyTooltipOpen">
            <template #trigger>
                <BaseButton
                    :theme="isCopied ? 'secondary' : 'tertiary'"
                    icon-only
                    :size="isNadiaChatRefreshEnabled ? 'sm' : 'md'"
                    :aria-label="t('chat.messageActions.copyMessage')"
                    :class="hoverRevealClass"
                    @click="handleCopyLine">
                    <i v-if="isNadiaChatRefreshEnabled" class="bi bi-copy" aria-hidden="true" />
                    <Copy v-else aria-hidden="true" />
                </BaseButton>
            </template>
            <template #content>
                <span class="inline-flex items-center gap-1" role="alert">
                    <i v-if="isCopied" class="bi bi-check text-base/none" aria-hidden="true"></i
                    >{{ isCopied ? t("chat.reactions.textCopied") : t("chat.reactions.copyToClipboard") }}
                </span>
            </template>
        </BaseTooltip>
        <BaseDropdownMenu v-model:open="isMoreMenuOpen" :sections="menuSections" side="top" :side-offset="8" align="start" content-class="min-w-50" @select="handleMoreMenuSelect">
            <template #trigger>
                <BaseButton
                    :theme="isMoreMenuOpen ? 'secondary' : 'tertiary'"
                    icon-only
                    :size="isNadiaChatRefreshEnabled ? 'sm' : 'md'"
                    :aria-label="t('chat.reactions.moreOptions')"
                    :class="hoverRevealClass"
                    @click.stop>
                    <i class="bi leading-none" :class="isNadiaChatRefreshEnabled ? 'bi-three-dots' : 'bi-three-dots-vertical'" aria-hidden="true"></i>
                </BaseButton>
            </template>
        </BaseDropdownMenu>

        <!-- Report issue modal -->
        <BaseDialog v-model:open="showReportIssueDialog" :title="t('chat.reactions.reportDialogTitle')" @close="hideReportIssueDialog" @update:open="handleReportDialogOpen">
            <BaseTextarea
                ref="reportIssueTextarea"
                v-model:input="reportIssueText"
                disable-autosize
                :aria-label="t('chat.reactions.reportInputLabel')"
                class="h-32 p-3 body border border-control-border rounded-lg resize-none focus-field"
                :placeholder="t('chat.reactions.whatWentWrong')" />
            <template #footer>
                <BaseButton theme="primary" size="sm" :disabled="isSubmittingReportIssue" @click="submitReportIssue">
                    {{ isSubmittingReportIssue ? t("chat.reactions.submitting") : t("chat.reactions.submit") }}
                </BaseButton>
            </template>
        </BaseDialog>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useNadiaChatRefreshEnabled } from "~vue/composables/useNadiaChatRefresh";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { useChatStore } from "~vue/stores/chatStore";
import DOMPurify from "dompurify";
import { Marked } from "marked";
import { computed, inject, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import BaseDialog from "./components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "./components/BaseDropdownMenu.vue";
import BaseTextarea from "./components/form/BaseTextarea.vue";
import { getFeedbackIssues } from "./Feedback/feedbackIssuesCache";
import FeedbackIssuesForm from "./Feedback/FeedbackIssuesForm.vue";
import Copy from "./icons/Copy.vue";
import HelpBubble from "./icons/HelpBubble.vue";
import ThumbsDown from "./icons/ThumbsDown.vue";
import ThumbsUp from "./icons/ThumbsUp.vue";
import type { ChatMessage } from "./types/chat";
import type { FeedbackTarget, ThumbDirection } from "./types/feedback";
import { logUserInteraction } from "./utils/logUtils";
import { noImageRenderer, sanitizeForChat } from "./utils/markdown";

interface Props {
    message: ChatMessage;
    isLastMessage?: boolean;
    thumbState?: ThumbDirection | null;
    isSubmitting?: boolean;
    hasExistingFeedback?: boolean;
}

interface FeedbackState {
    thumbState: ThumbDirection | null;
    isSubmitting: boolean;
}

interface FeedbackUpdate {
    messageId?: string;
    thumbState: ThumbDirection | null;
    hasComment: boolean;
}

interface FeedbackPayload {
    message_id?: string;
    feedback_kind: string;
    feedback_data: { value: number; issues?: string[] } | null;
    comment?: string | null;
}

const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid");
const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();
const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
    isLastMessage: false,
    thumbState: null,
    isSubmitting: false,
    hasExistingFeedback: false,
});

const { message } = props;

// Feedback state management
const feedbackState = ref<FeedbackState>({
    thumbState: null,
    isSubmitting: false,
});

// Simple computed properties based on internal state
const thumbUpActive = computed(() => feedbackState.value.thumbState === "up");
const thumbDownActive = computed(() => feedbackState.value.thumbState === "down");
const buttonsDisabled = computed(() => feedbackState.value.isSubmitting);
const hoverRevealClass = computed(() => {
    const shouldReveal = !props.isLastMessage && !props.hasExistingFeedback;
    return {
        "opacity-0 group-hover/chat:opacity-100": shouldReveal,
        ...(isNadiaChatRefreshEnabled.value ? { "group-focus-within/chat:opacity-100": shouldReveal } : {}),
    };
});

// Prefer the persisted ID and fall back to the streaming IDs to correlate playback.
const resolvedMessageId = computed(() => {
    const candidate = message.chat_message_id ?? message.id ?? message.message_id ?? message.temp_id;
    return candidate == null ? null : String(candidate);
});

// Compare all message IDs because streamed and persisted messages use different fields.
const isReadingThisMessage = computed(() => {
    const playingId = useChatStore.dictationPlayingMessageId;
    if (playingId == null) {
        return false;
    }
    const target = String(playingId);
    return [message.id, message.message_id, message.chat_message_id, message.temp_id].some((id) => id != null && String(id) === target);
});

function handleDictationToggle() {
    if (isReadingThisMessage.value) {
        // Pause: stop reading this message and turn dictation off.
        logUserInteraction("turned_dictation_off", {}, coachingSessionUuid);
        useChatStore.setDictationPlayingMessageId(null);
        if (useChatStore.dictationOn) {
            useChatStore.toggleDictation();
        }
        return;
    }
    // Play: mark this message as reading so the button flips to pause + selected immediately.
    logUserInteraction("turned_dictation_on", {}, coachingSessionUuid);
    if (!useChatStore.dictationOn) {
        useChatStore.toggleDictation();
    }
    useChatStore.setDictationPlayingMessageId(resolvedMessageId.value);
}

// Feedback issues dropdown state
const showFeedbackIssuesDialog = ref(false);
const isSubmittingFeedbackIssues = ref(false);

const feedbackTarget = computed<FeedbackTarget>(() => message.feedback_target ?? "chat_message");

// Report issue dialog state
const showReportIssueDialog = ref(false);
const reportIssueText = ref("");
const isSubmittingReportIssue = ref(false);
const reportIssueTextarea = useTemplateRef<InstanceType<typeof BaseTextarea>>("reportIssueTextarea");

// API composables
const thumbFeedbackRequest = useFetch("/api/chat/feedback/turn-feedback/", { immediate: false });

const emit = defineEmits<{
    thoughtProcessClicked: [message: ChatMessage];
    feedbackUpdated: [update: FeedbackUpdate];
}>();

const isMoreMenuOpen = ref(false);
const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: [
            { id: "report", type: "button", label: t("chat.reactions.reportIssue"), icon: "bi bi-info-circle" },
            { id: "thought-process", type: "button", label: t("chat.reactions.explainThoughtProcess"), icon: HelpBubble },
        ],
    },
]);

const { isShowing: isCopied, show: showCopied, hide: hideCopied } = useTimedShow({ duration: 3000 });
const isCopyTooltipOpen = ref(false);

function handleCopyTooltipOpen(open = false) {
    isCopyTooltipOpen.value = open;
    if (!open) {
        hideCopied();
    }
}

const handleReportClick = () => {
    // Reset on open, not on close: the dialog stays visible through the 150ms exit fade, and
    // resetting there wipes the textarea and re-enables Submit while the user can still see
    // (and for part of it, click) the dialog.
    reportIssueText.value = "";
    isSubmittingReportIssue.value = false;
    showReportIssueDialog.value = true;
    nextTick(() => {
        (reportIssueTextarea.value?.$el as HTMLTextAreaElement | undefined)?.focus();
    });
};

const handleMoreMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "report") {
        handleReportClick();
    } else if (item.id === "thought-process") {
        emit("thoughtProcessClicked", message);
    }
};

// A separator Nadia wrote around content that now renders as its own card reads as a stray rule once copied.
const DANGLING_RULE = /\n+\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

// Prose can be split across several lines by an interleaved action line, such as an email draft card.
const markdown = computed(() =>
    (message?.lines ?? [])
        .filter((line) => line.type === "md")
        .map((line) => line.content.replace(DANGLING_RULE, ""))
        .join("\n\n"),
);

async function handleCopyLine() {
    const plain = markdown.value;
    if (!plain) {
        return;
    }

    const parser = new Marked({ renderer: noImageRenderer });
    const html = sanitizeForChat(await parser.parse(plain));

    const data = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
    });
    await navigator.clipboard.write([data]);
    showCopied();
    isCopyTooltipOpen.value = true;

    // Log the copy event with message ID and session ID
    logUserInteraction(
        "message_copied",
        {
            chat_message_id: message.id,
        },
        coachingSessionUuid,
    );
}

// Main feedback handling function
const handleThumbFeedback = async (direction: ThumbDirection, comment: string | null = null, isCommentUpdate = false, issues: string[] = []) => {
    const messageId = message.chat_message_id;
    const currentState = feedbackState.value.thumbState;

    // For comment updates, preserve the existing thumbState
    const newThumbState = isCommentUpdate ? currentState : currentState === direction ? null : direction;

    // Only update optimistic state if not a comment update
    if (!isCommentUpdate) {
        feedbackState.value = {
            thumbState: newThumbState,
            isSubmitting: true,
        };
    }

    // Prepare API payload
    let feedbackData: FeedbackPayload["feedback_data"] = null;
    if (newThumbState) {
        feedbackData = { value: newThumbState === "up" ? 1 : 0 };
        if (issues.length > 0) {
            feedbackData.issues = [...issues];
        }
    }
    const payload: FeedbackPayload = {
        message_id: messageId.toString(),
        feedback_kind: "thumb",
        feedback_data: feedbackData,
    };

    if (newThumbState === null) {
        payload.comment = null;
    } else if (direction === "down" && comment) {
        payload.comment = DOMPurify.sanitize(comment);
    } else if (direction === "up") {
        payload.comment = null;
    }

    try {
        // API call
        await thumbFeedbackRequest.post(payload).execute();

        // Only update state if not a comment update
        if (!isCommentUpdate) {
            feedbackState.value = {
                thumbState: newThumbState,
                isSubmitting: false,
            };
        }

        // Log appropriate interaction based on context
        let eventName;
        if (isCommentUpdate) {
            eventName = "turn_feedback_comment_added";
        } else {
            eventName = newThumbState === null ? "turn_feedback_removed" : "turn_feedback_submitted";
        }

        logUserInteraction(
            eventName,
            {
                chat_message_id: messageId,
                feedback_kind: "thumb",
                feedback_value: newThumbState || direction,
                has_comment: !!comment,
            },
            coachingSessionUuid,
        );

        // Emit feedback update to parent for any external state management
        emit("feedbackUpdated", {
            messageId: messageId.toString(),
            thumbState: newThumbState,
            hasComment: !!comment,
        });
    } catch (error) {
        // Revert optimistic update on error (only if not a comment update)
        if (!isCommentUpdate) {
            feedbackState.value = {
                thumbState: currentState,
                isSubmitting: false,
            };
        }
        throw error; // Re-throw to handle in calling functions
    }
};

function handleThumbUpClick() {
    // Dismiss feedback popover if it's open
    dismissCommentPopover();
    handleThumbFeedback("up");
}

async function handleThumbDownClick() {
    if (thumbDownActive.value) {
        // If thumbs down is already active, remove it
        dismissCommentPopover();
        await handleThumbFeedback("down");
    } else {
        // Warm the issues list while the feedback POST is in flight, so the dialog opens
        // with its content already resolved instead of reflowing mid-enter-animation.
        // Best-effort: a failure here rejects (and evicts the cache); the form retries it
        // on mount and owns the user-facing error state, so this caller swallows it.
        void getFeedbackIssues(feedbackTarget.value).catch(() => {});

        // Send thumbs down feedback immediately
        await handleThumbFeedback("down");

        // Then show floating feedback panel for optional details
        showFeedbackIssuesDialog.value = true;
    }
}

function handleCommentPopoverChange(isOpen: boolean) {
    if (!isOpen) {
        dismissCommentPopover();
    }
}

async function handleFeedbackFormSave(payload: { issues: string[] | null; comments: string | null }) {
    // The dialog stays mounted (and focused) through the 150ms exit; pointer-events-none only
    // blocks the mouse, so Enter/Space on the still-focused Save could re-fire this after close.
    if (!showFeedbackIssuesDialog.value || isSubmittingFeedbackIssues.value || !feedbackState.value.thumbState) {
        return;
    }

    const issues = payload.issues ?? [];
    const comment = (payload.comments ?? "").trim() || null;
    const hasContent = !!comment || issues.length > 0;

    if (hasContent) {
        isSubmittingFeedbackIssues.value = true;
        try {
            await handleThumbFeedback(feedbackState.value.thumbState, comment, true, issues);
        } catch {
            isSubmittingFeedbackIssues.value = false;
            return;
        }
    }

    dismissCommentPopover();
}

function dismissCommentPopover() {
    showFeedbackIssuesDialog.value = false;
    isSubmittingFeedbackIssues.value = false;
}

const handleReportDialogOpen = (isOpen: boolean) => {
    if (!isOpen) {
        hideReportIssueDialog();
    }
};

function hideReportIssueDialog() {
    showReportIssueDialog.value = false;
}

async function submitReportIssue() {
    // Same keyboard-during-exit guard as handleFeedbackFormSave (cancel path: Submit is enabled).
    if (!showReportIssueDialog.value || isSubmittingReportIssue.value) {
        return;
    }

    isSubmittingReportIssue.value = true;
    const messageId = message.chat_message_id;

    const payload = {
        message_id: messageId,
        feedback_kind: "report_issue",
        feedback_data: null,
        comment: reportIssueText.value.trim() ? DOMPurify.sanitize(reportIssueText.value.trim()) : null,
    };

    try {
        await thumbFeedbackRequest.post(payload).execute();

        logUserInteraction(
            "message_report_issue_clicked",
            {
                chat_message_id: messageId,
                comment: reportIssueText.value.trim() || null,
            },
            coachingSessionUuid,
        );

        hideReportIssueDialog();
    } catch {
        isSubmittingReportIssue.value = false;
    }
}

// Initialize feedback state from existing feedback
const initializeFeedbackState = (existingFeedback?: { thumbState?: ThumbDirection | null }) => {
    feedbackState.value = {
        thumbState: existingFeedback?.thumbState || null,
        isSubmitting: false,
    };
};

// Expose method to allow parent to initialize state
defineExpose({
    initializeFeedbackState,
});
</script>
